/* BLOOMZ — product page (sections/bz-main-product.liquid). No dependencies. */
(() => {
  if (customElements.get('bz-product')) return;

  /* Same body as the contract's bzAdd (plus optional line item properties, used by the bundle tiers);
     also the fallback if bz-core.js has not defined window.bzAdd. */
  async function localAdd(variantId, qty = 1, properties = null) {
    const ids = [...document.querySelectorAll('cart-items-component')].map((e) => e.dataset.sectionId).filter(Boolean);
    const item = { id: Number(variantId), quantity: qty };
    if (properties) item.properties = properties;
    const r = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [item], sections: ids.join(',') }),
    });
    const data = await r.json();
    if (!r.ok) throw new Error(data.description || data.message || 'Add to cart failed');
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: {},
          sourceId: 'bz',
          data: { source: 'bz', itemCount: qty, variantId: String(variantId), sections: data.sections },
        },
      })
    );
    return data;
  }

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class BzProduct extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      try {
        this.data = JSON.parse(this.querySelector('script[data-variants]').textContent);
      } catch (e) {
        this.data = { sizeIndex: -1, options: [], variants: [] };
      }

      this.track = this.querySelector('[data-track]');
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.thumbs = this.querySelector('[data-thumbs]');
      this.thumbButtons = [...this.querySelectorAll('[data-thumb]')];
      this.prevBtn = this.querySelector('[data-gallery-prev]');
      this.nextBtn = this.querySelector('[data-gallery-next]');
      this.thumbsPrev = this.querySelector('[data-thumbs-prev]');
      this.thumbsNext = this.querySelector('[data-thumbs-next]');
      this.form = this.querySelector('form[data-form]') || this.querySelector('form');
      this.idInput = this.querySelector('[data-id-input]');
      this.buyButtons = [...this.querySelectorAll('[data-buy]')];
      this.mainButton = this.querySelector('[data-main-button]');
      this.bar = this.querySelector('[data-bar]');
      this.errorEl = this.querySelector('[data-error]');
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.activeIndex = 0;
      this.lockActive = false;
      this.loading = false;

      this.initState();
      this.initGallery();
      this.initOptions();
      this.initBundle();
      this.initBuy();
      this.initBar();
      this.initGuide();
      this.render({ initial: true });
    }

    disconnectedCallback() {
      this.barObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.setBodyPadding(false);
      this.unlockScroll();
    }

    /* ------------------------------------------------------------ state */

    initState() {
      this.state = this.data.options.map((option, i) => {
        const checked = this.querySelector(`input[data-option-index="${i}"]:checked`);
        if (checked) return checked.value;
        if (option.values.length === 1) return option.values[0];
        return null;
      });

      // Deep link fallback (?variant=ID) in case the server did not preselect it.
      const wanted = new URLSearchParams(window.location.search).get('variant');
      if (wanted && this.state.includes(null)) {
        const v = this.data.variants.find((x) => String(x.id) === wanted);
        if (v) this.state = [...v.options];
      }

      const first = this.variantForMedia();
      this.lastMediaId = first ? first.featured_media : null;
    }

    get variant() {
      if (this.state.includes(null)) return null;
      return this.data.variants.find((v) => v.options.every((value, i) => value === this.state[i])) || null;
    }

    /* First variant that matches every option already chosen (size may still be empty). */
    variantForMedia() {
      const matches = this.data.variants.filter((v) => v.options.every((value, i) => this.state[i] === null || value === this.state[i]));
      return matches.find((v) => v.featured_media) || matches[0] || null;
    }

    findWith(index, value) {
      return this.data.variants.filter((v) => v.options.every((val, i) => (i === index ? val === value : this.state[i] === null || i === this.data.sizeIndex || val === this.state[i])));
    }

    /* ------------------------------------------------------------ options */

    initOptions() {
      this.addEventListener('change', (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || !input.hasAttribute('data-option-index')) return;
        const index = Number(input.dataset.optionIndex);
        this.state[index] = input.value;

        // Changing a non-size option can make the chosen size not exist for that combination.
        const sizeIndex = this.data.sizeIndex;
        if (index !== sizeIndex && sizeIndex >= 0 && this.state[sizeIndex] !== null) {
          const exists = this.data.variants.some((v) => v.options.every((val, i) => val === this.state[i]));
          if (!exists) this.state[sizeIndex] = null;
        }

        this.clearError();
        this.render({});
      });
    }

    renderOptions() {
      const sizeIndex = this.data.sizeIndex;

      this.data.options.forEach((option, index) => {
        const label = this.querySelector(`[data-option-value="${index}"]`);
        if (label) label.textContent = this.state[index] ?? '';

        this.querySelectorAll(`input[data-option-index="${index}"]`).forEach((input) => {
          input.checked = input.value === this.state[index];

          if (index === sizeIndex) {
            const match = this.data.variants.find((v) => v.options.every((val, i) => (i === index ? val === input.value : this.state[i] === null || val === this.state[i])));
            input.disabled = !(match && match.available);
          } else {
            const anyAvailable = this.findWith(index, input.value).some((v) => v.available);
            input.nextElementSibling?.classList.toggle('is-soldout', !anyAvailable);
          }
        });
      });
    }

    /* ------------------------------------------------------------ bundle tiers (snippet bz-bundle-tiers) */

    /* Tiers are quantities of the same variant (1 · 2 · 6) paid through automatic discounts: the chosen tier
       sets the quantity, the painted total/compare (data-* of the radio) and the "Pair N Color/Size" properties. */
    initBundle() {
      this.bundle = this.querySelector('[data-bundle]');
      if (!this.bundle) return;
      this.bundle.addEventListener('change', (event) => {
        if (!(event.target instanceof HTMLInputElement) || !event.target.hasAttribute('data-tier')) return;
        this.clearError();
        this.renderBundle();
        this.renderButtons(this.variant);
      });
    }

    get tier() {
      if (!this.bundle) return null;
      return this.bundle.querySelector('input[data-tier]:checked');
    }

    get bundleQty() {
      const tier = this.tier;
      return tier ? Math.max(1, Number(tier.dataset.qty) || 1) : 1;
    }

    /* { "Pair 1 Color": "Black", "Pair 1 Size": "L", ... } from the selects of the chosen tier only. */
    bundleProperties() {
      const tier = this.tier;
      if (!tier) return null;
      const pairs = tier.parentElement?.querySelector('[data-pairs]');
      if (!pairs) return null;
      const props = {};
      pairs.querySelectorAll('select[data-prop]').forEach((select) => {
        if (select.value) props[select.dataset.prop] = select.value;
      });
      return Object.keys(props).length ? props : null;
    }

    renderBundle() {
      const tier = this.tier;
      if (!tier) return;
      const current = this.querySelector('[data-price-current]');
      const compare = this.querySelector('[data-price-compare]');
      const compareFmt = tier.dataset.compareFmt || '';
      if (current) {
        current.textContent = tier.dataset.totalFmt || current.textContent;
        current.classList.toggle('is-sale', Boolean(compareFmt));
      }
      if (compare) {
        compare.textContent = compareFmt;
        compare.hidden = !compareFmt;
      }
    }

    /* ------------------------------------------------------------ render */

    render({ initial = false }) {
      const variant = this.variant;
      const shown = variant || this.variantForMedia();

      this.renderOptions();
      this.renderPrice(shown);
      this.renderBundle();
      this.renderButtons(variant);

      if (this.idInput && shown) {
        const next = String(shown.id);
        const changed = this.idInput.value !== next;
        this.idInput.value = next;
        this.idInput.disabled = false;
        if (changed && !initial) this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (shown && shown.featured_media && shown.featured_media !== this.lastMediaId) {
        this.lastMediaId = shown.featured_media;
        const index = this.slides.findIndex((s) => s.dataset.mediaId === String(shown.featured_media));
        if (index >= 0) this.goTo(index, { lock: true, instant: initial });
      } else if (initial && variant && variant.featured_media) {
        const index = this.slides.findIndex((s) => s.dataset.mediaId === String(variant.featured_media));
        if (index > 0) this.goTo(index, { lock: true, instant: true });
      }

      if (!initial) {
        this.updateUrl(variant);
        this.dispatchEvent(new CustomEvent('bz:variant-change', { bubbles: true, detail: { variant } }));
      }
    }

    renderPrice(variant) {
      if (!variant) return;
      const current = this.querySelector('[data-price-current]');
      const compare = this.querySelector('[data-price-compare]');
      if (current) {
        current.textContent = variant.price_fmt;
        current.classList.toggle('is-sale', Boolean(variant.compare_fmt));
      }
      if (compare) {
        compare.textContent = variant.compare_fmt || '';
        compare.hidden = !variant.compare_fmt;
      }
      const shipping = this.querySelector('[data-shipping-text]');
      if (shipping) {
        const threshold = Number(shipping.dataset.threshold || 0);
        shipping.textContent = variant.price >= threshold ? shipping.dataset.yes : shipping.dataset.no;
      }
    }

    renderButtons(variant) {
      let text = this.dataset.textSelect;
      let mode = 'select';
      if (variant && variant.available) {
        text = this.dataset.textAdd;
        mode = 'ready';
      } else if (variant) {
        text = this.dataset.textSoldout;
        mode = 'soldout';
      }
      if (this.loading) text = this.dataset.textAdding;
      else if (mode === 'ready' && this.tier && this.tier.dataset.totalFmt) text = `${text} — ${this.tier.dataset.totalFmt}`;

      this.buyButtons.forEach((button) => {
        button.textContent = text;
        button.classList.toggle('is-ready', mode === 'ready');
        button.classList.toggle('is-soldout', mode === 'soldout');
        button.disabled = mode === 'soldout' || this.loading;
        if (this.loading) button.setAttribute('aria-busy', 'true');
        else button.removeAttribute('aria-busy');
      });
    }

    updateUrl(variant) {
      if (!window.history.replaceState) return;
      if (window.Shopify && window.Shopify.designMode) return;
      const url = new URL(window.location.href);
      if (variant) url.searchParams.set('variant', variant.id);
      else url.searchParams.delete('variant');
      window.history.replaceState(window.history.state, '', url.toString());
    }

    /* ------------------------------------------------------------ buy */

    initBuy() {
      if (this.form) this.form.addEventListener('submit', (event) => this.onBuy(event));
      this.querySelector('[data-bar-button]')?.addEventListener('click', (event) => this.onBuy(event));
    }

    async onBuy(event) {
      event.preventDefault();
      if (this.loading) return;
      const variant = this.variant;
      if (!variant) {
        this.promptChoice();
        return;
      }
      if (!variant.available) return;

      this.clearError();
      this.loading = true;
      this.renderButtons(variant);
      try {
        const properties = this.bundleProperties();
        const qty = this.bundleQty;
        if (properties || qty > 1) await localAdd(variant.id, qty, properties);
        else await (window.bzAdd ? window.bzAdd(variant.id) : localAdd(variant.id));
      } catch (error) {
        this.showError((error && error.message) || this.dataset.textError);
      } finally {
        this.loading = false;
        this.renderButtons(this.variant);
      }
    }

    promptChoice() {
      const index = this.state.indexOf(null);
      const fieldset = this.querySelector(`[data-option-fieldset="${index}"]`) || this.querySelector('[data-size-fieldset]');
      if (!fieldset) return;

      fieldset.scrollIntoView({ block: 'center', behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
      fieldset.classList.remove('is-flash');
      void fieldset.offsetWidth; // restart the animation
      fieldset.classList.add('is-flash');
      window.clearTimeout(this.flashTimer);
      this.flashTimer = window.setTimeout(() => fieldset.classList.remove('is-flash'), 1600);

      const live = fieldset.querySelector('[data-size-live]');
      if (live) {
        live.textContent = '';
        window.setTimeout(() => (live.textContent = this.dataset.textChoose || ''), 50);
      }
      fieldset.querySelector('input:not(:disabled)')?.focus({ preventScroll: true });
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
    }

    /* ------------------------------------------------------------ mobile fixed bar */

    initBar() {
      if (!this.bar || !this.mainButton || !('IntersectionObserver' in window)) return;
      this.barObserver = new IntersectionObserver(([entry]) => {
        if (!entry) return;
        this.barVisible = !entry.isIntersecting;
        this.bar.hidden = !this.barVisible;
        this.setBodyPadding(this.barVisible && this.mobile.matches);
      });
      this.barObserver.observe(this.mainButton);
      this.mobile.addEventListener('change', () => this.setBodyPadding(Boolean(this.barVisible) && this.mobile.matches));
    }

    /* Keeps the end of the page (footer) reachable above the fixed bar. */
    setBodyPadding(on) {
      if (on) {
        document.body.style.paddingBottom = '65px';
        this.paddedBody = true;
      } else if (this.paddedBody) {
        document.body.style.paddingBottom = '';
        this.paddedBody = false;
      }
    }

    /* ------------------------------------------------------------ gallery */

    initGallery() {
      if (!this.track || !this.slides.length) return;

      const release = () => (this.lockActive = false);
      ['pointerdown', 'wheel', 'touchstart'].forEach((type) => this.track.addEventListener(type, release, { passive: true }));

      let frame = 0;
      this.track.addEventListener(
        'scroll',
        () => {
          if (frame) return;
          frame = window.requestAnimationFrame(() => {
            frame = 0;
            this.syncGallery();
          });
        },
        { passive: true }
      );

      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.step(1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.step(-1);
        }
      });

      this.prevBtn?.addEventListener('click', () => this.step(-1));
      this.nextBtn?.addEventListener('click', () => this.step(1));

      this.thumbButtons.forEach((button) => {
        button.addEventListener('click', () => this.goTo(Number(button.dataset.index), { lock: true }));
      });

      if (this.thumbs) {
        this.thumbsPrev?.addEventListener('click', () => this.scrollThumbs(-1));
        this.thumbsNext?.addEventListener('click', () => this.scrollThumbs(1));
        this.thumbs.addEventListener('scroll', () => this.updateThumbArrows(), { passive: true });
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          this.syncGallery();
          this.updateThumbArrows();
        });
        this.resizeObserver.observe(this.track);
      }

      this.syncGallery();
      this.updateThumbArrows();
    }

    get perView() {
      const slide = this.slides[0];
      if (!slide || !slide.offsetWidth) return 1;
      return Math.max(1, Math.round(this.track.clientWidth / slide.offsetWidth));
    }

    leftIndex() {
      const slide = this.slides[0];
      if (!slide || !slide.offsetWidth) return 0;
      return Math.min(this.slides.length - 1, Math.max(0, Math.round(this.track.scrollLeft / slide.offsetWidth)));
    }

    step(direction) {
      this.lockActive = false;
      const max = Math.max(0, this.slides.length - this.perView);
      const target = Math.min(max, Math.max(0, this.leftIndex() + direction));
      this.goTo(target, {});
    }

    goTo(index, { lock = false, instant = false }) {
      const slide = this.slides[index];
      if (!slide) return;
      if (lock) {
        this.lockActive = true;
        this.setActive(index);
      }
      const behavior = instant || this.reducedMotion.matches ? 'auto' : 'smooth';
      this.track.scrollTo({ left: slide.offsetLeft, behavior });
    }

    syncGallery() {
      // A thumb the shopper clicked (or a variant photo) stays active until they scroll the viewer themselves:
      // with two photos per view the last photo never reaches the left edge.
      if (!this.lockActive) this.setActive(this.leftIndex());
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      if (this.prevBtn) this.prevBtn.disabled = this.track.scrollLeft <= 1;
      if (this.nextBtn) this.nextBtn.disabled = this.track.scrollLeft >= maxScroll - 1;
    }

    setActive(index) {
      this.activeIndex = index;
      this.thumbButtons.forEach((button, i) => {
        const active = i === index;
        button.classList.toggle('is-active', active);
        if (active) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
      this.revealThumb(index);
    }

    /* Scrolls only the thumbnail list (never the page). */
    revealThumb(index) {
      const button = this.thumbButtons[index];
      if (!button || !this.thumbs) return;
      const behavior = this.reducedMotion.matches ? 'auto' : 'smooth';
      const box = this.thumbs.getBoundingClientRect();
      const rect = button.getBoundingClientRect();
      if (this.mobile.matches) {
        if (rect.left < box.left) this.thumbs.scrollBy({ left: rect.left - box.left, behavior });
        else if (rect.right > box.right) this.thumbs.scrollBy({ left: rect.right - box.right, behavior });
      } else {
        const pad = 31; // keeps the thumb clear of the up/down arrows
        if (rect.top < box.top + pad) this.thumbs.scrollBy({ top: rect.top - box.top - pad, behavior });
        else if (rect.bottom > box.bottom - pad) this.thumbs.scrollBy({ top: rect.bottom - box.bottom + pad, behavior });
      }
    }

    scrollThumbs(direction) {
      if (!this.thumbs) return;
      const amount = Math.max(134, this.thumbs.clientHeight - 134);
      this.thumbs.scrollBy({ top: direction * amount, behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
    }

    updateThumbArrows() {
      if (!this.thumbs) return;
      const max = this.thumbs.scrollHeight - this.thumbs.clientHeight;
      if (this.thumbsPrev) this.thumbsPrev.hidden = this.thumbs.scrollTop <= 1;
      if (this.thumbsNext) this.thumbsNext.hidden = max <= 1 || this.thumbs.scrollTop >= max - 1;
    }

    /* ------------------------------------------------------------ size guide modal */

    initGuide() {
      this.querySelectorAll('[data-guide-open]').forEach((opener) => {
        const dialog = this.querySelector(`#${CSS.escape(opener.dataset.guideOpen)}`);
        if (!dialog) return;
        opener.addEventListener('click', () => this.openGuide(dialog, opener));
      });

      this.querySelectorAll('dialog[data-guide]').forEach((dialog) => {
        dialog.querySelector('[data-guide-close]')?.addEventListener('click', () => dialog.close());

        // Click on the backdrop closes.
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) dialog.close();
        });

        // Focus trap (showModal already makes the rest of the page inert; this keeps Tab cycling inside).
        dialog.addEventListener('keydown', (event) => {
          if (event.key !== 'Tab') return;
          const items = [...dialog.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
          if (!items.length) {
            event.preventDefault();
            return;
          }
          const first = items[0];
          const last = items[items.length - 1];
          if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        });

        dialog.addEventListener('close', () => {
          this.unlockScroll();
          const opener = this.guideOpener;
          this.guideOpener = null;
          opener?.focus({ preventScroll: true });
        });

        dialog.querySelectorAll('[data-unit]').forEach((button) => {
          button.addEventListener('click', () => {
            dialog.dataset.units = button.dataset.unit;
            dialog.querySelectorAll('[data-unit]').forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
          });
        });
      });
    }

    openGuide(dialog, opener) {
      if (dialog.open) return;
      this.guideOpener = opener;
      this.lockScroll();
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      dialog.scrollTop = 0;
      dialog.querySelector('[data-guide-close]')?.focus({ preventScroll: true });
    }

    lockScroll() {
      if (this.scrollLocked) return;
      const root = document.documentElement;
      this.scrollLocked = true;
      this.prevOverflow = root.style.overflow;
      this.prevPaddingRight = root.style.paddingRight;
      const gap = window.innerWidth - root.clientWidth;
      root.style.overflow = 'hidden';
      if (gap > 0) root.style.paddingRight = `${gap}px`;
    }

    unlockScroll() {
      if (!this.scrollLocked) return;
      const root = document.documentElement;
      root.style.overflow = this.prevOverflow || '';
      root.style.paddingRight = this.prevPaddingRight || '';
      this.scrollLocked = false;
    }
  }

  customElements.define('bz-product', BzProduct);
})();
